import { useState, useEffect, useCallback } from 'react';
import { WinCheckbox, WinRadio } from './WinComponents';
import InfoFinModal from './InfoFinModal';
import MoneyInput from './MoneyInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RevenusTab = ({ formData, updateRevenus, setFormData, totalRevenusReq, totalRevenusFam, totalDeductions, noSeqDaj }) => {
  const [infoFinOpen, setInfoFinOpen] = useState(false);
  const [infoFinSource, setInfoFinSource] = useState('R');

  const openInfoFin = (source) => {
    setInfoFinSource(source);
    setInfoFinOpen(true);
  };

  const applyConsolidation = useCallback((data) => {
    if (!data || !data.totaux_requerant) return;
    const r = data.totaux_requerant;
    const f = data.totaux_famille;
    setFormData(prev => ({...prev, revenus: {
      ...prev.revenus,
      emploi_req: r["emploi"] || 0,
      assurance_emploi_req: r["assurance emploi"] || 0,
      securite_revenu_req: r["sécurité du revenu"] || 0,
      csst_req: (r["c.s.s.t."] || 0) + (r["cnesst"] || 0),
      saaq_req: r["s.a.a.q."] || 0,
      pension_vieillesse_req: r["pension de vieillesse"] || 0,
      pension_alimentaire_req: r["pension alimentaire"] || 0,
      pension_alimentaire_enfants_req: r["pens. alim. enfants"] || 0,
      rrq_rpc_req: r["rrq / prc"] || 0,
      autres_revenus_req: r["autres revenus"] || 0,
      emploi_fam: f["emploi"] || 0,
      assurance_emploi_fam: f["assurance emploi"] || 0,
      securite_revenu_fam: f["sécurité du revenu"] || 0,
      csst_fam: (f["c.s.s.t."] || 0) + (f["cnesst"] || 0),
      saaq_fam: f["s.a.a.q."] || 0,
      pension_vieillesse_fam: f["pension de vieillesse"] || 0,
      pension_alimentaire_fam: f["pension alimentaire"] || 0,
      pension_alimentaire_enfants_fam: f["pens. alim. enfants"] || 0,
      rrq_rpc_fam: f["rrq / prc"] || 0,
      autres_revenus_fam: f["autres revenus"] || 0,
    }}));
  }, [setFormData]);

  // Charge (et persiste) les totaux consolidés pour une année donnée dans adda3XX.
  const loadConsolidation = useCallback(async (annee) => {
    if (!noSeqDaj || !annee) return;
    try {
      const res = await fetch(`${API_URL}/api/infofin/consolider/${noSeqDaj}?annee=${annee}`, {method: 'POST'});
      const data = await res.json();
      applyConsolidation(data);
    } catch (e) { console.error(e); }
  }, [noSeqDaj, applyConsolidation]);

  const refAnnee = formData.revenus.annee_reference || String(new Date().getFullYear() - 1);

  // Après tout ajout/édition/suppression dans le modal détaillé, on réaligne
  // TOUJOURS le résumé (colonnes adda3XX) sur l'année de référence de l'onglet,
  // afin que les montants du formulaire détaillé et de l'onglet concordent.
  const handleConsolidate = useCallback((data, annee) => {
    const currentAnnee = formData.revenus.annee_reference || String(new Date().getFullYear() - 1);
    if (annee === currentAnnee && data) {
      applyConsolidation(data);
    } else {
      loadConsolidation(currentAnnee);
    }
  }, [formData.revenus.annee_reference, applyConsolidation, loadConsolidation]);

  const handleAnneeChange = (type) => {
    const currentYear = new Date().getFullYear();
    const newAnnee = type === 'courante' ? String(currentYear) : String(currentYear - 1);
    setFormData(prev => ({...prev, revenus: {
      ...prev.revenus,
      annee_type: type === 'courante' ? 'courante' : 'precedente',
      annee_reference: newAnnee
    }}));
    loadConsolidation(newAnnee);
  };
  const fmtMoney = (v) => {
    const num = Number(v) || 0;
    if (num === 0) return '';
    const parts = num.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join('.') + ' $';
  };

  const moneyPlaceholder = '0.00 $';

  const parseMoney = (v) => parseFloat(String(v).replace(/[^\d.-]/g,'')) || 0;

  // Legacy CodeDaj.vb:351-358 — Calcul du Grand Total :
  //   Si art.7(1) ou 7(2) sélectionné dans l'onglet Conjoint  ⇒
  //       les revenus du conjoint sont EXCLUS du Grand Total
  //       (regardless of chkFamInclus / famille_incluse).
  //   Sinon (vide) ⇒
  //       on inclut les revenus de la famille si chkFamInclus est coché.
  const art67 = (formData.conjoint?.art_6_ou_7 || '').trim();
  const art7Active = art67 === '7(1)' || art67 === '7(2)';
  const includeFam = !art7Active && formData.revenus.famille_incluse;
  const grandTotal = totalRevenusReq + (includeFam ? totalRevenusFam : 0) - totalDeductions;

  // Ligne: label a gauche, input a droite (lecture seule)
  const RoRow = ({label, value, testId}) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5}}>
      <label className="win-field-label" style={{flex: '0 0 55%', marginBottom: 0, textAlign: 'left', paddingRight: 2, fontSize: 13, whiteSpace: 'nowrap'}}>{label}</label>
      <input className="win-input" readOnly value={fmtMoney(value)} placeholder={moneyPlaceholder} data-testid={testId} style={{flex: '0 0 42%', backgroundColor: '#f0f0f0', padding: '3px 6px', height: '24px', textAlign: 'right'}} />
    </div>
  );

  // Ligne editable (deductions)
  const EditRow = ({label, field, testId}) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5}}>
      <label className="win-field-label" style={{flex: '0 0 55%', marginBottom: 0, textAlign: 'left', paddingRight: 2, fontSize: 13, whiteSpace: 'nowrap'}}>{label}</label>
      <MoneyInput
        value={formData.revenus[field] || 0}
        onChange={v => updateRevenus(field, v)}
        testId={testId}
        style={{flex: '0 0 42%', padding: '3px 6px', height: '24px', textAlign: 'right'}} />
    </div>
  );

  // Ligne total
  const TotalRow = ({label, value, testId}) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5, borderTop: '1px solid #999', paddingTop: 4, marginTop: 2}}>
      <label className="win-field-label" style={{flex: '0 0 55%', marginBottom: 0, textAlign: 'left', paddingRight: 2, fontWeight: 'bold', fontSize: 13, whiteSpace: 'nowrap'}}>{label}</label>
      <input className="win-input" readOnly value={fmtMoney(value)} placeholder={moneyPlaceholder} data-testid={testId} style={{flex: '0 0 42%', backgroundColor: '#e8e8e8', fontWeight: 'bold', padding: '3px 6px', height: '24px', textAlign: 'right'}} />
    </div>
  );

  return (
    <div className="win-tab-content compact" data-testid="tab-content-revenus">

      {/* BOUTON MODIFIER LES REVENUS */}
      <div style={{marginBottom: 8}}>
        <button className="win-btn-action" onClick={() => openInfoFin('R')} data-testid="btn-infofin-req" style={{fontWeight: 'bold'}}>
          MODIFIER LES REVENUS
        </button>
      </div>

      {/* 4 COLONNES: REQUERANT | FAMILLE | DEDUCTIONS | OCCUPATION */}
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', alignItems: 'stretch'}}>

        {/* COLONNE 1: REQUERANT */}
        <fieldset className="win-groupbox compact" style={{margin: 0, height: '100%'}}>
          <legend>Revenus - Requérant</legend>
          <RoRow label="Emploi" value={formData.revenus.emploi_req} testId="input-emploi-req" />
          <RoRow label="Assurance emploi" value={formData.revenus.assurance_emploi_req} testId="input-ass-emploi-req" />
          <RoRow label="Sécurité du revenu" value={formData.revenus.securite_revenu_req} testId="input-sec-rev-req" />
          <RoRow label="CNESST" value={formData.revenus.csst_req} testId="input-cnesst-req" />
          <RoRow label="S.A.A.Q." value={formData.revenus.saaq_req} testId="input-saaq-req" />
          <RoRow label="Pension vieillesse" value={formData.revenus.pension_vieillesse_req} testId="input-pension-vieill-req" />
          <RoRow label="Pension aliment." value={formData.revenus.pension_alimentaire_req} testId="input-pension-alim-req" />
          <RoRow label="Pens. alim. enfants" value={formData.revenus.pension_alimentaire_enfants_req} testId="input-pension-alim-enf-req" />
          <RoRow label="RRQ/RPC" value={formData.revenus.rrq_rpc_req} testId="input-rrq-rpc-req" />
          <RoRow label="Autres revenus" value={formData.revenus.autres_revenus_req} testId="input-autres-rev-req" />
          <TotalRow label="Total" value={totalRevenusReq} testId="total-revenus-req" />
        </fieldset>

        {/* COLONNE 2: FAMILLE */}
        <fieldset className="win-groupbox compact" style={{margin: 0, height: '100%'}}>
          <legend style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: 'calc(100% - 24px)'}}>
            <span>
              Revenus - Famille
              {art7Active && (
                <span
                  data-testid="badge-art7-exclus"
                  title={`Les revenus du conjoint sont exclus du Grand Total car art. ${art67} est sélectionné (onglet Conjoint).`}
                  style={{marginLeft: 8, padding: '1px 6px', background: '#ffc107', color: '#333', borderRadius: 0, fontSize: 13, fontWeight: 'bold', border: '1px solid #555'}}
                >
                  art. {art67} — exclus
                </span>
              )}
            </span>
            <span onClick={e => e.stopPropagation()} style={{display: 'flex', alignItems: 'center', color: 'white'}}>
              <label style={{display: 'flex', alignItems: 'center', gap: 6, cursor: art7Active ? 'not-allowed' : 'pointer', fontSize: 13, opacity: art7Active ? 0.55 : 1}}>
                <input
                  type="checkbox"
                  checked={formData.revenus.famille_incluse}
                  disabled={art7Active}
                  onChange={e => updateRevenus('famille_incluse', e.target.checked)}
                  style={{width: 16, height: 16, accentColor: 'white', background: 'white', appearance: 'auto', WebkitAppearance: 'checkbox'}}
                  data-testid="chk-famille-incluse"
                />
                Inclus
              </label>
            </span>
          </legend>
          <RoRow label="Emploi" value={formData.revenus.emploi_fam} testId="input-emploi-fam" />
          <RoRow label="Assurance emploi" value={formData.revenus.assurance_emploi_fam} testId="input-ass-emploi-fam" />
          <RoRow label="Sécurité du revenu" value={formData.revenus.securite_revenu_fam} testId="input-sec-rev-fam" />
          <RoRow label="CNESST" value={formData.revenus.csst_fam} testId="input-cnesst-fam" />
          <RoRow label="S.A.A.Q." value={formData.revenus.saaq_fam} testId="input-saaq-fam" />
          <RoRow label="Pension vieillesse" value={formData.revenus.pension_vieillesse_fam} testId="input-pension-vieill-fam" />
          <RoRow label="Pension aliment." value={formData.revenus.pension_alimentaire_fam} testId="input-pension-alim-fam" />
          <RoRow label="Pens. alim. enfants" value={formData.revenus.pension_alimentaire_enfants_fam} testId="input-pension-alim-enf-fam" />
          <RoRow label="RRQ/RPC" value={formData.revenus.rrq_rpc_fam} testId="input-rrq-rpc-fam" />
          <RoRow label="Autres revenus" value={formData.revenus.autres_revenus_fam} testId="input-autres-rev-fam" />
          <TotalRow label="Total" value={totalRevenusFam} testId="total-revenus-fam" />
        </fieldset>

        {/* COLONNE 3: ANNEE DE REFERENCE + DEDUCTIONS */}
        <div style={{display: 'flex', flexDirection: 'column', gap: 8, height: '100%'}}>
          {/* Annee de reference */}
          <fieldset className="win-groupbox compact" style={{margin: 0}}>
            <legend>Année de référence</legend>
            <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
              <label className="win-field-label" style={{marginBottom: 0}}>Année</label>
              <input className="win-input" readOnly value={formData.revenus.annee_reference || new Date().getFullYear() - 1} data-testid="input-annee-ref" style={{width: '60px', padding: '3px 6px', height: '24px', backgroundColor: '#e8e8e8', fontWeight: 'bold', textAlign: 'center'}} />
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
              <WinRadio name="annee_type" value="precedente" checked={formData.revenus.annee_type !== 'courante'} onChange={() => handleAnneeChange('precedente')} label="Précédente" />
              <WinRadio name="annee_type" value="courante" checked={formData.revenus.annee_type === 'courante'} onChange={() => handleAnneeChange('courante')} label="Courante" />
              {formData.revenus.annee_type === 'courante' && (
                <input className="win-input" placeholder="Raison..." value={formData.revenus.raison_annee_courante || ''} onChange={e => setFormData(prev => ({...prev, revenus: {...prev.revenus, raison_annee_courante: e.target.value}}))} style={{padding: '3px 6px', height: '24px', flex: 1}} />
              )}
            </div>
          </fieldset>

          {/* Deductions */}
          <fieldset className="win-groupbox compact" style={{margin: 0, flex: 1}}>
            <legend>Déductions annualisées</legend>
            <EditRow label="Déficience" field="deficience" testId="input-deficience" />
            <EditRow label="Frais de garde" field="frais_garde" testId="input-frais-garde" />
            <EditRow label="Pens. alim. versée" field="pension_alimentaire_payee" testId="input-pension-alim-payee" />
            <div style={{display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5}}>
              <label className="win-field-label" style={{flex: '0 0 55%', marginBottom: 0, textAlign: 'left', paddingRight: 2, fontSize: 13, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4}}>Pens. alim. enfants <span style={{fontSize: 12, color: '#666'}}>({formData.nb_enfants} x 6 000$)</span></label>
              <input className="win-input" readOnly value={fmtMoney(formData.nb_enfants * 6000)} data-testid="input-deduc-pens-enf" style={{flex: '0 0 42%', backgroundColor: '#f0f0f0', padding: '3px 6px', height: '24px', textAlign: 'right'}} />
            </div>
            <EditRow label="Frais scolaires" field="frais_scolaires" testId="input-frais-scolaires" />
            <div style={{display: 'flex', alignItems: 'center', gap: 4, borderTop: '1px solid #999', paddingTop: 4, marginTop: 2}}>
              <label className="win-field-label" style={{flex: '0 0 55%', marginBottom: 0, textAlign: 'left', paddingRight: 2, fontWeight: 'bold', fontSize: 13, whiteSpace: 'nowrap'}}>Total</label>
              <input className="win-input" readOnly value={fmtMoney(totalDeductions)} placeholder={moneyPlaceholder} data-testid="total-deductions" style={{flex: '0 0 42%', backgroundColor: '#e8e8e8', fontWeight: 'bold', padding: '3px 6px', height: '24px', textAlign: 'right'}} />
            </div>
          </fieldset>

          {/* Grand Total */}
          <fieldset className="win-groupbox compact" style={{margin: 0, backgroundColor: '#f8f9fa'}}>
            <legend>Revenu annuel - Admissibilité</legend>
            <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
              <label className="win-field-label" style={{flex: '0 0 55%', marginBottom: 0, textAlign: 'left', paddingRight: 2, fontWeight: 'bold', fontSize: 13, whiteSpace: 'nowrap'}}>Grand Total</label>
              <input className="win-input" readOnly value={fmtMoney(grandTotal)} placeholder={moneyPlaceholder} data-testid="grand-total-revenus" style={{flex: '0 0 42%', backgroundColor: '#d4edda', fontWeight: 'bold', fontSize: '15px', textAlign: 'right', padding: '3px 6px', height: '24px'}} />
            </div>
          </fieldset>
        </div>

        {/* COLONNE 4: OCCUPATION ET SOURCES DE REVENU */}
        <div style={{display: 'flex', flexDirection: 'column', gap: 8, height: '100%'}}>
          <fieldset className="win-groupbox compact" style={{margin: 0, height: '100%'}}>
            <legend>Occupation / Sources</legend>
            <div className="win-field" style={{marginBottom: 6}}>
              <label className="win-field-label" style={{fontSize: 13}}>Occupation</label>
              <input className="win-input" value={formData.revenus.occupation || ''} onChange={e => setFormData(prev => ({...prev, revenus: {...prev.revenus, occupation: e.target.value}}))} data-testid="input-occupation" style={{padding: '3px 6px', height: '24px'}} />
            </div>
            <div className="win-field" style={{marginBottom: 6}}>
              <label className="win-field-label" style={{fontSize: 13}}>Employeur</label>
              <input className="win-input" value={formData.revenus.employeur || ''} onChange={e => setFormData(prev => ({...prev, revenus: {...prev.revenus, employeur: e.target.value}}))} data-testid="input-employeur" style={{padding: '3px 6px', height: '24px'}} />
            </div>
            <div className="win-field" style={{marginBottom: 4}}>
              <label className="win-field-label" style={{fontSize: 13}}>Nom source 1</label>
              <input className="win-input" value={formData.revenus.nom_employeur_1 || ''} onChange={e => setFormData(prev => ({...prev, revenus: {...prev.revenus, nom_employeur_1: e.target.value}}))} data-testid="input-nom-employeur-1" style={{padding: '3px 6px', height: '24px'}} />
            </div>
            <div className="win-field" style={{marginBottom: 6}}>
              <label className="win-field-label" style={{fontSize: 13}}>Adresse</label>
              <textarea className="win-textarea" rows={2} value={formData.revenus.adr_employeur_1 || ''} onChange={e => setFormData(prev => ({...prev, revenus: {...prev.revenus, adr_employeur_1: e.target.value}}))} data-testid="input-adr-employeur-1" style={{padding: '3px 6px'}} />
            </div>
            <div className="win-field" style={{marginBottom: 4}}>
              <label className="win-field-label" style={{fontSize: 13}}>Nom source 2</label>
              <input className="win-input" value={formData.revenus.nom_employeur_2 || ''} onChange={e => setFormData(prev => ({...prev, revenus: {...prev.revenus, nom_employeur_2: e.target.value}}))} data-testid="input-nom-employeur-2" style={{padding: '3px 6px', height: '24px'}} />
            </div>
            <div className="win-field">
              <label className="win-field-label" style={{fontSize: 13}}>Adresse</label>
              <textarea className="win-textarea" rows={2} value={formData.revenus.adr_employeur_2 || ''} onChange={e => setFormData(prev => ({...prev, revenus: {...prev.revenus, adr_employeur_2: e.target.value}}))} data-testid="input-adr-employeur-2" style={{padding: '3px 6px'}} />
            </div>
          </fieldset>
        </div>
      </div>

      <InfoFinModal
        isOpen={infoFinOpen}
        onClose={() => { setInfoFinOpen(false); loadConsolidation(refAnnee); }}
        noseqdaj={noSeqDaj}
        sourceId={infoFinSource}
        initialAnneeType={formData.revenus.annee_type === 'courante' ? 'courante' : 'precedente'}
        onConsolidate={handleConsolidate}
        hasConjoint={!!(formData.conjoint?.nom)}
        hasLien1={!!(formData.lien1?.nom || formData.lien1?.type_lien)}
        hasLien2={!!(formData.lien2?.nom || formData.lien2?.type_lien)}
      />
    </div>
  );
};

export default RevenusTab;
